import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { closeVideoLightBoxModalAction } from "store/modals/reducer.actions";
import Lightbox from "yet-another-react-lightbox";
import Video from "yet-another-react-lightbox/plugins/video";
import "yet-another-react-lightbox/styles.css";
import VideoThumbnail from "./VideoThumbnail.jpeg";

type VideoSlide = {
  type: "video";
  width: number;
  height: number;
  poster: string;
  sources: { src: string; type: string }[];
};

export function VideoLightBox() {
  const [videoSlides, setVideoSlides] = useState<VideoSlide[]>([]);

  const { videoLightBoxModal } = useSelector(
    (state: RootState) => state.modals
  );
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeVideoLightBoxModalAction());
  };

  // Pick a conservative MIME from extension, but also include alternatives so the browser can choose.
  const buildSources = (
    url: string,
    fileType?: string
  ): VideoSlide["sources"] => {
    const ext = (fileType || url.split(".").pop() || "").toLowerCase();

    // Primary guess
    const primary =
      ext === "mov"
        ? "video/quicktime"
        : ext === "mp4"
          ? "video/mp4"
          : `video/${ext}`;

    // Some browsers won’t play QuickTime MOV even if codecs are H.264/AAC.
    // Offering multiple type hints gives them a chance to try.
    const candidates = Array.from(
      new Set(
        [primary, ext === "mov" ? "video/mp4" : "", "video/quicktime"].filter(
          Boolean
        )
      )
    );

    return candidates.map((type) => ({ src: url, type }));
  };

  const slide: VideoSlide | null = useMemo(() => {
    if (!videoLightBoxModal?.isOpen || !videoLightBoxModal?.data?.filePath)
      return null;

    const { filePath, fileType } = videoLightBoxModal.data;

    return {
      type: "video",
      width: 3840,
      height: 2560,
      poster: VideoThumbnail,
      sources: buildSources(filePath, fileType),
    };
  }, [
    videoLightBoxModal?.isOpen,
    videoLightBoxModal?.data?.filePath,
    videoLightBoxModal?.data?.fileType,
  ]);

  useEffect(() => {
    if (slide) setVideoSlides([slide]);
    else setVideoSlides([]);
  }, [slide]);

  return (
    <Lightbox
      key={slide?.sources?.[0]?.src || "video-lightbox"} // force re-init if URL changes
      open={!!videoLightBoxModal?.isOpen}
      close={handleClose}
      slides={videoSlides}
      plugins={[Video]}
      render={{ iconPrev: () => null, iconNext: () => null }}
      video={{
        controls: true,
        playsInline: true,
        autoPlay: false,
        loop: false,
        muted: false,
        // Helpful when loading from S3 across origins:
        crossOrigin: "anonymous",
        // Let the browser read duration/first frame, enables range requests:
        preload: "metadata",
        // Some browsers respect this for DL buttons:
        controlsList: "",
        disablePictureInPicture: false,
        disableRemotePlayback: false,
      }}
    />
  );
}
